import React, { useEffect, useRef } from "react";
import Button from "../Button/Button";
import "./Offcanvas.scss";

export interface OffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  position?: "start" | "end" | "top" | "bottom";
  width?: string;
  className?: string;
  showHeader?: boolean;
  footer?: React.ReactNode;
}

const Offcanvas: React.FC<OffcanvasProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = "end",
  width = "520px",
  className = "",
  showHeader = true,
  footer,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(
    `offcanvas-title-${Math.random().toString(36).slice(2)}`,
  );

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Focus trap — move focus into panel when opened
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const focusable = panelRef.current.querySelector<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      focusable?.focus();
    }
  }, [isOpen]);

  const sizeStyle: React.CSSProperties =
    position === "top" || position === "bottom" ? { height: width } : { width };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`offcanvasBackdrop${isOpen ? " offcanvasBackdropVisible" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        className={`offcanvasContainer offcanvasContainer--${position}${isOpen ? " offcanvasContainerOpen" : ""}${className ? ` ${className}` : ""}`}
        style={sizeStyle}
      >
        {showHeader && (
          <div className="offcanvasHeader">
            <h5 id={titleId.current} className="offcanvasTitle">
              {title}
            </h5>
            <Button
              className="closeButton"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </Button>
          </div>
        )}

        <div className="offcanvasBody">{children}</div>

        {footer && <div className="offcanvasFooter">{footer}</div>}
      </div>
    </>
  );
};

export default Offcanvas;
